import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Joyride, { type CallBackProps, type Step } from 'react-joyride';
import { useNavigate } from 'react-router';

import { useGetLoggedInUser } from '@revelio/auth';
import { PagePaths, useTourStore } from '@revelio/core';

import {
  GLOBAL_AI_OVERLAY_BOTTOM,
  GLOBAL_AI_OVERLAY_HEIGHT,
  GLOBAL_AI_OVERLAY_WIDTH,
  GLOBAL_AI_VIEWPORT_RIGHT_PX,
} from '../CustomDash/constants/panelWidths';
import { useGlobalAIAssistantFeatureEnabled } from '../global-ai-assistant/use-global-ai-assistant-feature-enabled';
import {
  AI_SPOTLIGHT_ANCHOR_ID,
  buildTourSteps,
  getTourStepData,
  type RenderedNavSection,
  TourPhase,
} from './tour.config';
import { pollUntil } from './tour.utils';
import { TourTooltip } from './tour-tooltip';
import { useIsTourDone } from './use-is-tour-done';
import { useTourRunner } from './use-tour-runner';

// The global FAB sits 24px off the bottom (AIAssistantFAB's default
// `fixedBottomPx`, which GlobalAIAssistantHost leaves unset); the drawer sits
// GLOBAL_AI_OVERLAY_BOTTOM off it. The union box therefore runs from the FAB's
// bottom edge up over the drawer's full height — the drawer's height plus the
// gap down to the FAB.
const AI_FAB_BOTTOM_PX = 24;
const AI_ANCHOR_EXTRA_HEIGHT_PX =
  Number.parseInt(GLOBAL_AI_OVERLAY_BOTTOM, 10) - AI_FAB_BOTTOM_PX;

/**
 * Invisible box spanning the assistant FAB and the open chat drawer, so the
 * closing step can spotlight both at once. Positioned from the same constants
 * that place the FAB and drawer, so it tracks them without measuring. Present
 * throughout the tour; only the AI step targets it.
 */
function AiSpotlightAnchor() {
  return (
    <div
      id={AI_SPOTLIGHT_ANCHOR_ID}
      aria-hidden="true"
      style={{
        position: 'fixed',
        right: `${GLOBAL_AI_VIEWPORT_RIGHT_PX}px`,
        bottom: `${AI_FAB_BOTTOM_PX}px`,
        width: GLOBAL_AI_OVERLAY_WIDTH,
        height: `calc(${GLOBAL_AI_OVERLAY_HEIGHT} + ${AI_ANCHOR_EXTRA_HEIGHT_PX}px)`,
        pointerEvents: 'none',
      }}
    />
  );
}

const NAV_SECTION_ATTRIBUTE = 'data-tour-nav-section';
// ExpandedNav tags each child link with its own name; the tour reads the names
// back to adapt the Applications step to the user's enrichment tools.
const NAV_CHILD_ATTRIBUTE = 'data-nav-child';
const AI_FAB_OPEN_LABEL = 'Open AI chat';
const AI_FAB_CLOSE_LABEL = 'Close AI chat';

/** ~5s at `pollUntil`'s 100ms cadence — the rail renders well inside that. */
const NAV_RENDER_POLL_ATTEMPTS = 50;

/**
 * Joyride measures the spotlight from the target's current box. Pinning the
 * rail open is a CSS width transition, so give the browser two frames to lay
 * it out before the runner polls — otherwise the first section step spotlights
 * the 40px collapsed rail.
 */
const settleLayout = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

/**
 * The control-panel sections this user's nav renders, each with the child
 * links beneath it. Child links stay in the DOM whether or not the section is
 * expanded (collapse only hides them via CSS), so this reads correctly at any
 * point. Drives both which section steps exist and how Applications adapts.
 */
function readRenderedNavSections(): RenderedNavSection[] {
  return Array.from(
    document.querySelectorAll(`[${NAV_SECTION_ATTRIBUTE}]`)
  ).flatMap((element) => {
    const name = element.getAttribute(NAV_SECTION_ATTRIBUTE);
    if (!name) {
      return [];
    }
    const childNames = Array.from(
      element.querySelectorAll(`[${NAV_CHILD_ATTRIBUTE}]`)
    ).flatMap((child) => {
      const childName = child.getAttribute(NAV_CHILD_ATTRIBUTE);
      return childName ? [childName] : [];
    });
    return [{ name, childNames }];
  });
}

/**
 * Clicks the AI assistant's floating button to reach the state we want.
 *
 * The tour renders outside `GlobalAIAssistantProvider`, so it drives the
 * drawer the way a user would rather than reaching for the context. The
 * button's accessible name carries the current state, which is also how we
 * avoid toggling a drawer that's already in the right state.
 */
function setAiDrawerOpen(shouldBeOpen: boolean): void {
  const wantedLabel = shouldBeOpen ? AI_FAB_OPEN_LABEL : AI_FAB_CLOSE_LABEL;
  const fab = document.querySelector(`[aria-label="${wantedLabel}"]`);
  if (fab instanceof HTMLElement) {
    fab.click();
  }
}

/**
 * The guided product tour: themed rail (`ExpandedNav`), page tab strip, and the
 * global AI assistant. Steps are built from the sections the user's permissions
 * actually render, so the tour never describes a workspace they can't open.
 */
function Tour() {
  const navigate = useNavigate();

  const { loggedInUser } = useGetLoggedInUser();

  const isTourState = useTourStore((s) => s.isTour);
  const isTourDone = useIsTourDone();
  const setIsTour = useTourStore((s) => s.setIsTour);
  const setIsNavPinned = useTourStore((s) => s.setIsNavPinned);
  const setTourNavSection = useTourStore((s) => s.setTourNavSection);

  // Gates the closing step: the assistant FAB/drawer only exist when this is on.
  const assistantEnabled = useGlobalAIAssistantFeatureEnabled();

  // Auto-start on first login — the new layout has no welcome prompt to launch
  // the tour. `tour_completed` is written on skip/finish, so it won't re-fire.
  //
  // Held until the rail has rendered its sections: the step list is captured
  // from them once the tour starts, so starting before `ExpandedNav` mounts
  // yields a tour with no control-panel steps. Starting anyway on timeout keeps
  // a rail that never renders from swallowing the tour outright.
  useEffect(() => {
    if (isEmpty(loggedInUser)) return;
    if (isTourState || isTourDone) return;

    return pollUntil(
      () => readRenderedNavSections().length > 0,
      () => setIsTour(true),
      () => setIsTour(true),
      { maxAttempts: NAV_RENDER_POLL_ATTEMPTS }
    );
  }, [loggedInUser, isTourState, isTourDone, setIsTour]);

  // Captured once when the tour starts: the step list has to stay stable while
  // Joyride is running, and the rail is fully mounted by then.
  const [navSections, setNavSections] = useState<RenderedNavSection[]>([]);
  useEffect(() => {
    if (!isTourState) return;
    setNavSections(readRenderedNavSections());
  }, [isTourState]);

  const steps = useMemo(
    () => buildTourSteps(navSections, { assistantEnabled }),
    [navSections, assistantEnabled]
  );

  /**
   * Puts the app into the state a step needs. Keyed off the step we're moving
   * *to* rather than the one we're leaving, so forwards and backwards share
   * one description of each step's world.
   */
  const applyStepChoreography = useCallback(
    async (destination: Step | undefined) => {
      const data = destination && getTourStepData(destination);
      if (!data) return;

      if (data.phase !== TourPhase.AiChat) {
        setAiDrawerOpen(false);
      }

      switch (data.phase) {
        case TourPhase.Intro:
          setIsNavPinned(false);
          setTourNavSection(null);
          navigate(`${PagePaths.LANDING}`);
          break;

        case TourPhase.ControlPanelSection:
          setIsNavPinned(true);
          setTourNavSection(data.navSection ?? null);
          await settleLayout();
          break;

        case TourPhase.Entities:
          // First company-page step: collapse the rail (so the spotlight
          // doesn't linger over a section that's about to close) and navigate
          // onto the company page the rest of this phase walks.
          setTourNavSection(null);
          setIsNavPinned(false);
          navigate(`/${PagePaths.COMPANY_COMPOSITIONS}`);
          break;

        case TourPhase.PageTabs:
        case TourPhase.Filters:
          setIsNavPinned(false);
          setTourNavSection(null);
          break;

        case TourPhase.AiChat:
          setIsNavPinned(false);
          setTourNavSection(null);
          setAiDrawerOpen(true);
          await settleLayout();
          break;
      }
    },
    [navigate, setIsNavPinned, setTourNavSection]
  );

  const onStepForward = useCallback(
    ({ index }: CallBackProps) => applyStepChoreography(steps[index + 1]),
    [applyStepChoreography, steps]
  );

  const onStepBack = useCallback(
    ({ index }: CallBackProps) => applyStepChoreography(steps[index - 1]),
    [applyStepChoreography, steps]
  );

  const onComplete = useCallback(() => {
    setIsNavPinned(false);
    setTourNavSection(null);
    setAiDrawerOpen(false);
  }, [setIsNavPinned, setTourNavSection]);

  const {
    stepIndexState,
    tourRenderKey,
    stepsWithOverrides,
    handleJoyrideCallback,
  } = useTourRunner({ steps, onStepForward, onStepBack, onComplete });

  if (!isTourState) {
    return null;
  }

  return (
    <>
      <AiSpotlightAnchor />
      <Joyride
        key={tourRenderKey}
        continuous
        showSkipButton
        callback={handleJoyrideCallback}
        steps={stepsWithOverrides}
        run={isTourState}
        stepIndex={stepIndexState}
        tooltipComponent={TourTooltip}
        styles={{
          options: {
            arrowColor: 'var(--ui-surface-floating)',
            overlayColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 10000,
          },
          // Joyride rounds the spotlight to 4px; square it so the cut-out sits
          // flush against the hard-cornered surfaces it highlights, matching
          // the tooltip. `spotlightLegacy` is the same box drawn via box-shadow
          // instead of a mask, so it needs the override too.
          spotlight: { borderRadius: 0 },
          spotlightLegacy: { borderRadius: 0 },
        }}
        floaterProps={{
          disableAnimation: true,
          styles: {
            // react-floater renders the arrow as its own element in a padding
            // gutter beside the tooltip, so any border or shadow on the card
            // stops dead at the card's edge and draws a seam across the arrow's
            // base. Both move onto the floater instead, where the filter traces
            // the union outline of card + arrow and they read as one shape.
            floater: {
              filter:
                'drop-shadow(0 0 1px var(--ui-border-floating)) drop-shadow(0 12px 24px rgb(0 0 0 / 0.22))',
            },
            arrow: { length: 9, spread: 18 },
          },
        }}
        // Keep the user in the tour until they skip or finish it.
        disableOverlayClose
        disableCloseOnEsc
        hideCloseButton
      />
    </>
  );
}

export default Tour;
